import { addBreadcrumb } from '@sentry/vue';
import { logError } from '@utils/error-tracking';

type StoreModule = Record<string, () => unknown>;

async function lazyImportStore<T extends StoreModule, K extends keyof T>(
	importPromise: Promise<T>,
	storeName: K
): Promise<ReturnType<T[K]>> {
	addBreadcrumb({
		category: 'store',
		message: `Loading store: ${storeName as string}`,
		level: 'info'
	});

	try {
		const module = (await importPromise) as T | undefined;

		if (!module) {
			const error = new Error(`Store module loaded but returned undefined: ${storeName as string}`);
			logError(error, 'error', {
				key: 'lazyImportStore',
				data: { storeName }
			});
			throw error;
		}

		const storeFactory = module[storeName];
		if (!storeFactory) {
			const error = new Error(`Store export "${storeName as string}" not found in module`);
			logError(error, 'error', {
				key: 'lazyImportStore',
				data: { storeName, availableExports: Object.keys(module) }
			});
			throw error;
		}

		return storeFactory() as ReturnType<T[K]>;
	} catch (error) {
		addBreadcrumb({
			category: 'store',
			message: `Failed to load store: ${storeName as string}`,
			level: 'error',
			data: { error: error instanceof Error ? error.message : String(error) }
		});

		if (error instanceof Error && !error.message.includes('Store module loaded')) {
			logError(error, 'error', {
				key: 'lazyImportStore',
				data: { storeName }
			});
		}

		throw error;
	}
}

export const useLazyAccountClosureStore = () =>
	lazyImportStore(import('@stores/account-closure'), 'useAccountClosureStore');

export const useLazyAccountSetupChecklistStore = () =>
	lazyImportStore(import('@stores/account-setup-checklist'), 'useAccountSetupChecklistStore');

export const useLazyAccreditationStore = () =>
	lazyImportStore(import('@stores/accreditation'), 'useAccreditationStore');

export const useLazyAdvisorStore = () => lazyImportStore(import('@stores/advisor'), 'useAdvisorStore');

export const useLazyAutoInvestStore = () => lazyImportStore(import('@stores/auto-invest'), 'useAutoInvestStore');

export const useLazyCustomPlansStore = () => lazyImportStore(import('@stores/custom-plans'), 'useCustomPlansStore');

export const useLazyEmailStore = () => lazyImportStore(import('@stores/email'), 'useEmailStore');

export const useLazyInvestmentEntityStore = () =>
	lazyImportStore(import('@stores/investment-entity'), 'useInvestmentEntityStore');

export const useLazyInvestmentGoalsStore = () =>
	lazyImportStore(import('@stores/investment-goals'), 'useInvestmentGoalsStore');

export const useLazyInvestStore = () => lazyImportStore(import('@stores/invest'), 'useInvestStore');

export const useLazyIpoStore = () => lazyImportStore(import('@stores/ipo'), 'useIpoStore');

export const useLazyIraStore = () => lazyImportStore(import('@stores/ira'), 'useIraStore');

export const useLazyLoginStore = () => lazyImportStore(import('@stores/login'), 'useLoginStore');

export const useLazyModularDashboardStore = () =>
	lazyImportStore(import('@stores/modular-dashboard'), 'useModularDashboardStore');

export const useLazyPerformancePortfolioStore = () =>
	lazyImportStore(import('@stores/performance-portfolio'), 'usePerformancePortfolioStore');

export const useLazyPlansStore = () => lazyImportStore(import('@stores/plans'), 'usePlansStore');

export const useLazyRedemptionRequestStore = () =>
	lazyImportStore(import('@stores/redemption-request'), 'useRedemptionRequestStore');

export const useLazyRiaUpgradeStore = () => lazyImportStore(import('@stores/ria-upgrade'), 'useRiaUpgradeStore');

export const useLazyTaxStore = () => lazyImportStore(import('@stores/tax'), 'useTaxStore');

export const useLazyUserStore = () => lazyImportStore(import('@stores/user'), 'useUserStore');
